/**
 * Host (TV) end-game view for Scotland Yard.
 *
 * Three things in this view:
 *   1. A big winner banner across the top.
 *   2. The board on the left:
 *        - Static mode (default): Mr. X's full path drawn as a connected
 *          colored polyline through every station he visited, with stop
 *          numbers at each node, plus detective final positions.
 *        - Replay mode (when scrubber is engaged): every pawn — including
 *          Mr. X — animated to the position they occupied at that frame.
 *   3. A right-side sidebar mirroring the live `GameSidebar` but with the
 *      full Mr. X transport trail (every station number revealed) and per-
 *      round station ids next to the transport icons.
 *   4. A scrubber bar across the bottom of the map: round 0 (setup) to
 *      round N (last frame). Click/drag the slider to seek; the play/pause
 *      button auto-advances at ~1s per frame. Smooth interpolation on
 *      pawns is delegated to BoardCanvas (which animates whenever a pawn's
 *      target station changes).
 *
 * Graceful degradation: legacy games saved before the `replay` field was
 * added will hit this view with `state.public.replay == null`. We render
 * the winner banner + a "no replay data" notice and skip the map polyline
 * + scrubber. Detective final positions are still visible from the live
 * state's `detectives[i].station`.
 *
 * IMPORTANT: this view re-uses BoardCanvas with two new props (`replayFrame`,
 * `mrxPath`). We chose extending BoardCanvas over building a parallel
 * ReplayBoardCanvas because (a) most of the rendering — map, stations,
 * pawn animation, click handling — is identical, and (b) the diff is
 * additive and surgical: ~50 lines for the path + ~30 for replay-frame
 * overrides. A separate component would have duplicated ~400 lines.
 */

import { ActionIcon, Badge, Box, Button, Group, Paper, ScrollArea, Slider, Stack, Text, ThemeIcon, Title } from '@mantine/core'
import { IconPlayerPause, IconPlayerPlay, IconPlayerSkipBack, IconPlayerSkipForward, IconRefresh } from '@tabler/icons-react'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { rematch } from '../../lobby/service'
import { PLAYER_COLOR_HEX } from '@mdev/common'
import type { Room, DetectiveState, GameState, LogTransport, MrxLogEntry, ReplayData } from '@mdev/common'
import { BoardCanvas } from './BoardCanvas'
import type { MrxPath, ReplayFrame } from './BoardCanvas'
import type { ScotlandYardSettings } from '../module'
import { SY, SY_ACCENT_YELLOW, SY_WIN_GREEN } from '../theme'
import { TRANSPORT_COLOR, TRANSPORT_LABEL, TransportIcon } from './TransportIcon'
import { stationColor } from './stationTransports'

/** Auto-advance interval when the user hits Play. Roughly one frame/second. */
const AUTO_ADVANCE_MS = 1100

interface Props {
  room: Room<ScotlandYardSettings>
  state: { public: GameState; mrxStation: number }
}

export function HostFinishedView( props: Props ) {
  const { state, room } = props
  const publicState = state.public
  const replay: ReplayData | null = publicState.replay ?? null

  const navigate = useNavigate()
  const [ rematchPending, setRematchPending ] = useState( false )
  async function onPlayAgain() {
    if ( room.nextCode ) {
      navigate( `/games/${ room.gameId }/host/${ room.nextCode }` )
      return
    }
    setRematchPending( true )
    try {
      const { code } = await rematch( room.code, room.gameId, room.settings )
      navigate( `/games/${ room.gameId }/host/${ code }` )
    } catch {
      setRematchPending( false )
    }
  }

  // The Mr. X log entry that "arrives at" frame i (i = 1..N). Index 0
  // is the starting position (no transport).
  const transports: ( LogTransport | null )[] = useMemo( () => {
    const log = publicState.mrx.transportLog
    if ( !replay ) return [ null, ...log.map( ( e ) => e.transport ) ]
    // Frame count = mrxStations.length. Entry i (1-indexed) corresponds to
    // log entry i-1. If log is shorter than expected (legacy / partial
    // data), pad with 'black' as a neutral fallback.
    const out: ( LogTransport | null )[] = [ null ]
    for ( let i = 1; i < replay.mrxStations.length; i++ ) {
      const logEntry: MrxLogEntry | undefined = log[ i - 1 ]
      out.push( logEntry ? logEntry.transport : 'black' )
    }
    return out
  }, [ replay, publicState.mrx.transportLog ] )

  // Staggered timeline: each round becomes a Mr. X step (he moves, detectives
  // frozen) followed by a detective step (they respond, Mr. X frozen) — so a
  // detective who was right behind Mr. X no longer looks like it teleports onto
  // him in a single frame.
  const steps = useMemo<ReplayFrame[]>(
    () => ( replay ? buildSteps( publicState, replay ) : [] ),
    [ replay, publicState ],
  )
  const totalFrames = steps.length
  const maxFrame = Math.max( 0, totalFrames - 1 )

  const [ frame, setFrame ] = useState<number>( maxFrame )
  const [ isPlaying, setIsPlaying ] = useState( false )

  // If the replay payload arrives late (subscription race), snap the
  // initial frame to the end of the game.
  useEffect( () => {
    setFrame( ( cur ) => ( cur > maxFrame ? maxFrame : cur ) )
  }, [ maxFrame ] )

  // Auto-advance loop. Stops at the end (clamps + flips Play off).
  useEffect( () => {
    if ( !isPlaying ) return
    if ( frame >= maxFrame ) {
      setIsPlaying( false )
      return
    }
    const id = window.setTimeout( () => {
      setFrame( ( cur ) => {
        const next = cur + 1
        if ( next >= maxFrame ) {
          // Reach the end and stop.
          setIsPlaying( false )
          return maxFrame
        }
        return next
      } )
    }, AUTO_ADVANCE_MS )
    return () => window.clearTimeout( id )
  }, [ isPlaying, frame, maxFrame ] )

  // The "static" mode is when the user hasn't scrubbed away from the end,
  // i.e. they're still looking at the final position. Showing the polyline
  // there is the most useful default. Once they start scrubbing, we hide
  // the static path so the moving pawns don't get lost in the line.
  const isAtEnd = frame === maxFrame
  const showStaticPath = isAtEnd && replay !== null && totalFrames >= 2

  const replayFrame: ReplayFrame | null = useMemo( () => {
    if ( !replay || isAtEnd ) return null // static mode: render live state
    return steps[ frame ] ?? null
  }, [ replay, isAtEnd, frame, steps ] )

  const mrxPath: MrxPath | null = useMemo( () => {
    if ( !showStaticPath || !replay ) return null
    return { stations: replay.mrxStations, transports }
  }, [ showStaticPath, replay, transports ] )

  return (
    <Stack
      gap={ 0 }
      style={ { width: '100%', height: '100%', minHeight: 0, background: SY.pageBg } }
    >
      <WinnerBanner
        state={ publicState }
        onPlayAgain={ onPlayAgain }
        playAgainPending={ rematchPending }
      />

      <Group
        gap={ 0 }
        wrap="nowrap"
        style={ { flex: 1, minHeight: 0, position: 'relative' } }
      >
        <Stack gap={ 0 } style={ { flex: 1, minWidth: 0, height: '100%' } }>
          <Box style={ { flex: 1, minHeight: 0 } }>
            <BoardCanvas
              options={ { showMap: true, showEdges: false, showStations: true, dimMap: true } }
              selectedId={ null }
              onSelect={ () => { /* no-op: post-game view is non-interactive */ } }
              gameState={ publicState }
              replayFrame={ replayFrame }
              mrxPath={ mrxPath }
            />
          </Box>
          { replay && totalFrames >= 2 ? (
            <Scrubber
              frame={ frame }
              maxFrame={ maxFrame }
              isPlaying={ isPlaying }
              setFrame={ setFrame }
              setPlaying={ setIsPlaying }
            />
          ) : (
            <Box
              p="md"
              style={ { background: SY.panelBg, borderTop: `1px solid ${ SY.border }` } }
            >
              <Text c="dimmed" size="sm" ta="center">
                { replay
                  ? 'Game ended on the very first move — no replay timeline.'
                  : 'Replay data not available for this game.' }
              </Text>
            </Box>
          ) }
        </Stack>
        <FinishedSidebar
          state={ publicState }
          mrxStations={ replay?.mrxStations ?? null }
        />
      </Group>
    </Stack>
  )
}

// ---------------------------------------------------------------------------
// Build the staggered replay timeline.
// ---------------------------------------------------------------------------

/**
 * Expand the denormalized `replay` payload into an ordered list of frames that
 * play back turn-by-turn:
 *
 *   start → [Mr. X sub-move(s), then detectives respond] per turn
 *
 * Within a turn Mr. X moves first (detectives frozen at their previous spot),
 * then the detectives move (Mr. X frozen). This matches real play order, so a
 * capture reads as "Mr. X steps here → a detective steps onto him" rather than
 * both pawns jumping at once.
 *
 * Index conventions: `mrxStations` ticks once per sub-move (index 0 = start);
 * each detective history ticks once per turn (index 0 = start). A double move
 * is two consecutive log entries (`doubleMoveSecond` flags the second), so it
 * contributes two Mr. X steps but still one detective response.
 */
function buildSteps( state: GameState, replay: ReplayData ): ReplayFrame[] {
  const mrxStations = replay.mrxStations
  const log = state.mrx.transportLog
  const dets = state.detectives

  const detsAt = ( detIdx: number ): Record<string, number> => {
    const out: Record<string, number> = {}
    for ( const d of dets ) {
      const h = replay.detectiveStationsByUid[ d.uid ]
      if ( !h || h.length === 0 ) {
        if ( d.station != null ) out[ d.uid ] = d.station
        continue
      }
      out[ d.uid ] = h[ Math.min( detIdx, h.length - 1 ) ]
    }
    return out
  }

  const steps: ReplayFrame[] = []
  let mrxIdx = 0
  let detIdx = 0
  const push = () =>
    steps.push( {
      frameIndex: steps.length,
      mrxStation: mrxStations[ Math.min( mrxIdx, mrxStations.length - 1 ) ],
      detectiveStationsByUid: detsAt( detIdx ),
    } )

  push() // starting positions

  let i = 0
  while ( i < log.length ) {
    const isDouble = i + 1 < log.length && log[ i + 1 ].doubleMoveSecond
    const subMoves = isDouble ? 2 : 1
    // Mr. X's sub-move(s): he advances, detectives stay put.
    for ( let j = 0; j < subMoves; j++ ) {
      mrxIdx++
      push()
    }
    // Detectives respond: they advance, Mr. X stays put.
    detIdx++
    push()
    i += subMoves
  }

  // Drop consecutive no-op frames (e.g. the final detective "response" when the
  // game actually ended on Mr. X's move, or a round where no detective moved).
  const deduped = steps.filter( ( s, idx ) => idx === 0 || !sameFrame( s, steps[ idx - 1 ] ) )
  deduped.forEach( ( s, idx ) => { s.frameIndex = idx } )
  return deduped
}

function sameFrame( a: ReplayFrame, b: ReplayFrame ): boolean {
  if ( a.mrxStation !== b.mrxStation ) return false
  for ( const k of Object.keys( a.detectiveStationsByUid ) ) {
    if ( a.detectiveStationsByUid[ k ] !== b.detectiveStationsByUid[ k ] ) return false
  }
  return true
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function WinnerBanner( {
  state,
  onPlayAgain,
  playAgainPending,
}: {
  state: GameState
  onPlayAgain: () => void
  playAgainPending: boolean
} ) {
  const winner = state.winner
  const headline = winner
    ? winner.team === 'detectives'
      ? 'Detectives win!'
      : 'Mr. X escapes!'
    : 'Game over'
  const reasonLabel = winner ? humanReadableReason( winner.reason, state ) : null
  const headlineColor = winner?.team === 'detectives' ? SY_WIN_GREEN : SY_ACCENT_YELLOW

  return (
    <Box
      p="md"
      style={ {
        background: SY.panelBg,
        borderBottom: `2px solid ${ headlineColor }`,
        boxShadow: `0 4px 24px ${ headlineColor }40`,
        position: 'relative',
      } }
    >
      <Stack gap={ 4 } align="center">
        <Title
          order={ 1 }
          c="bright"
          style={ { fontSize: 44, fontWeight: 900, color: headlineColor, letterSpacing: 0.5 } }
        >
          { headline }
        </Title>
        { reasonLabel && (
          <Text c="dimmed" size="md">
            { reasonLabel }
          </Text>
        ) }
      </Stack>
      <Button
        color="yellow"
        size="md"
        leftSection={ <IconRefresh size={ 18 } /> }
        loading={ playAgainPending }
        onClick={ onPlayAgain }
        style={ { position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)' } }
      >
        Play again
      </Button>
    </Box>
  )
}

function humanReadableReason( reason: string, state: GameState ): string {
  switch ( reason ) {
    case 'capture': {
      // The capturing detective is on Mr. X's last station.
      const captureStation = state.replay
        ? state.replay.mrxStations[ state.replay.mrxStations.length - 1 ]
        : state.mrx.lastRevealedStation
      return captureStation != null
        ? `Captured at #${ captureStation } in round ${ state.round }`
        : `Captured in round ${ state.round }`
    }
    case 'mrx_cornered':
      return `Mr. X was cornered with no legal moves`
    case 'all_detectives_stranded':
      return `Every detective ran out of legal moves`
    case 'survived_24_rounds':
      return `Mr. X survived all ${ state.round } rounds`
    default:
      return reason
  }
}

interface ScrubberProps {
  frame: number
  maxFrame: number
  isPlaying: boolean
  setFrame: ( f: number ) => void
  setPlaying: ( p: boolean ) => void
}

function Scrubber( props: ScrubberProps ) {
  const { frame, maxFrame, isPlaying, setFrame, setPlaying } = props
  const handlePlayPause = () => {
    if ( frame >= maxFrame ) {
      // Restart from the beginning if we're already at the end.
      setFrame( 0 )
      setPlaying( true )
      return
    }
    setPlaying( !isPlaying )
  }

  return (
    <Box
      p="md"
      style={ { background: SY.panelBg, borderTop: `1px solid ${ SY.border }` } }
    >
      <Group gap="md" wrap="nowrap" align="center">
        <ActionIcon
          variant="subtle"
          color="gray"
          size="lg"
          onClick={ () => {
            setPlaying( false )
            setFrame( 0 )
          } }
          aria-label="Jump to start"
        >
          <IconPlayerSkipBack size={ 22 } />
        </ActionIcon>
        <ActionIcon
          variant="filled"
          color="yellow"
          size="xl"
          onClick={ handlePlayPause }
          aria-label={ isPlaying ? 'Pause' : 'Play' }
        >
          { isPlaying ? <IconPlayerPause size={ 24 } /> : <IconPlayerPlay size={ 24 } /> }
        </ActionIcon>
        <ActionIcon
          variant="subtle"
          color="gray"
          size="lg"
          onClick={ () => {
            setPlaying( false )
            setFrame( maxFrame )
          } }
          aria-label="Jump to end"
        >
          <IconPlayerSkipForward size={ 22 } />
        </ActionIcon>

        <Text c="dimmed" size="sm" w={ 80 }>
          Frame { frame } / { maxFrame }
        </Text>

        <Slider
          flex={ 1 }
          min={ 0 }
          max={ maxFrame }
          step={ 1 }
          value={ frame }
          onChange={ ( v ) => {
            setPlaying( false )
            setFrame( Math.max( 0, Math.min( maxFrame, v ) ) )
          } }
          color="yellow"
          marks={ buildScrubberMarks( maxFrame ) }
          styles={ { markLabel: { color: 'var(--mantine-color-dimmed)', fontSize: 11 } } }
        />
      </Group>
    </Box>
  )
}

/** Build a sparse set of marks under the scrubber. Avoid clutter on long games. */
function buildScrubberMarks( maxFrame: number ): { value: number; label: string }[] {
  if ( maxFrame <= 0 ) return [ { value: 0, label: '0' } ]
  const out: { value: number; label: string }[] = []
  // Aim for ~6 marks across the slider.
  const step = Math.max( 1, Math.round( maxFrame / 6 ) )
  for ( let v = 0; v <= maxFrame; v += step ) {
    out.push( { value: v, label: String( v ) } )
  }
  if ( out[ out.length - 1 ].value !== maxFrame ) {
    out.push( { value: maxFrame, label: String( maxFrame ) } )
  }
  return out
}

// ---------------------------------------------------------------------------
// Sidebar — like GameSidebar but reveals every station number.
// ---------------------------------------------------------------------------

interface SidebarProps {
  state: GameState
  /**
   * Mr. X's full station history (the same array stored as
   * `state.replay.mrxStations`). Lined up index-for-index with the
   * transport log: index 0 = starting station, index k = post-(k-1)'th
   * log-entry station. `null` if the replay payload isn't available.
   */
  mrxStations: number[] | null
}

function FinishedSidebar( props: SidebarProps ) {
  const { state, mrxStations } = props
  return (
    <Stack
      gap={ 0 }
      h="100%"
      style={ { background: SY.panelBg, borderLeft: `1px solid ${ SY.border }`, width: 360 } }
    >
      <Box p="md" style={ { borderBottom: `1px solid ${ SY.border }` } }>
        <Title order={ 3 } c="bright">Final review</Title>
        <Text size="sm" c="dimmed" mt={ 2 }>
          Mr. X's full path is now visible.
        </Text>
      </Box>

      <ScrollArea flex={ 1 } p="md">
        <Stack gap="md">
          <MrxFullTrail state={ state } mrxStations={ mrxStations } />
          <DetectivesPanel state={ state } />
        </Stack>
      </ScrollArea>
    </Stack>
  )
}

function MrxFullTrail( { state, mrxStations }: { state: GameState; mrxStations: number[] | null } ) {
  const { mrx } = state
  return (
    <Paper
      p="sm"
      radius="md"
      style={ { background: SY.surfaceBg, border: `1px solid ${ SY.border }` } }
    >
      <Group gap="xs" mb="sm" wrap="nowrap">
        <Box
          style={ {
            width: 44, height: 44, borderRadius: '50%', background: SY.border,
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24,
          } }
        >
          { mrx.avatar }
        </Box>
        <Stack gap={ 0 }>
          <Text c="bright" fw={ 700 }>{ mrx.displayName }</Text>
          <Text size="xs" c="dimmed">Full transport trail</Text>
        </Stack>
      </Group>

      { mrxStations && mrxStations.length > 0 && (
        <Group gap="xs" mb="xs" wrap="nowrap">
          <Badge size="sm" color="gray" variant="light">Start</Badge>
          <Text size="sm" c="bright" fw={ 600 }>#{ mrxStations[ 0 ] }</Text>
        </Group>
      ) }

      <Stack gap={ 4 }>
        { mrx.transportLog.map( ( entry, idx ) => {
          // mrxStations is 1-indexed relative to log entries: log[0] -> mrxStations[1].
          const station = mrxStations ? mrxStations[ idx + 1 ] ?? null : entry.revealedStation
          return (
            <RevealedTrailRow
              key={ `${ entry.round }-${ entry.doubleMoveSecond ? 'b' : 'a' }-${ idx }` }
              entry={ entry }
              station={ station }
            />
          )
        } ) }
        { mrx.transportLog.length === 0 && (
          <Text c="dimmed" size="sm">No moves recorded.</Text>
        ) }
      </Stack>
    </Paper>
  )
}

function RevealedTrailRow( { entry, station }: { entry: MrxLogEntry; station: number | null } ) {
  return (
    <Group
      gap="xs"
      wrap="nowrap"
      style={ {
        background: SY.revealRowBg,
        border: `1px solid ${ SY.borderSoft }`,
        borderRadius: 6,
        padding: '4px 8px',
      } }
    >
      <Text size="xs" c="dimmed" w={ 24 } ta="right" fw={ 600 }>
        { entry.round }
      </Text>
      <ThemeIcon size="sm" variant="transparent">
        <TransportIcon transport={ entry.transport } size={ 18 } />
      </ThemeIcon>
      <Text size="sm" c={ TRANSPORT_COLOR[ entry.transport ] } flex={ 1 }>
        { TRANSPORT_LABEL[ entry.transport ] }{ entry.doubleMoveSecond ? ' (2×)' : '' }
      </Text>
      { station != null ? (
        <Badge size="sm" variant="filled" style={ { background: stationColor( station ), color: '#fff' } }>
          #{ station }
        </Badge>
      ) : (
        <Badge size="sm" color="gray" variant="light">?</Badge>
      ) }
    </Group>
  )
}

function DetectivesPanel( { state }: { state: GameState } ) {
  return (
    <Stack gap="xs">
      <Text size="xs" c="dimmed" tt="uppercase" fw={ 600 } lts={ 0.5 }>
        Detectives
      </Text>
      { state.detectives.map( ( d ) => (
        <DetectiveCard key={ d.uid } detective={ d } />
      ) ) }
    </Stack>
  )
}

function DetectiveCard( { detective }: { detective: DetectiveState } ) {
  const colorHex = PLAYER_COLOR_HEX[ detective.color ]
  const finalStation = detective.station
  return (
    <Paper
      p="sm"
      radius="md"
      style={ { background: SY.surfaceBg, border: `1px solid ${ SY.border }` } }
    >
      <Group justify="space-between" wrap="nowrap">
        <Group gap="xs" wrap="nowrap">
          <Box
            style={ {
              width: 36, height: 36, borderRadius: '50%', background: colorHex,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 20, flexShrink: 0,
            } }
          >
            { detective.avatar }
          </Box>
          <Stack gap={ 0 }>
            <Text c="bright" fw={ 600 } size="sm">{ detective.displayName }</Text>
            <Text size="xs" c="dimmed">
              { detective.stranded
                ? 'Stranded'
                : finalStation != null
                  ? `Final: #${ finalStation }`
                  : 'Out' }
            </Text>
          </Stack>
        </Group>
      </Group>
    </Paper>
  )
}
